import React, { memo, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { format, isBefore, startOfDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import type { DoctorShift } from '../../../../../types/doctorShift';
import {
  TimelineContainer,
  EmptyState,
  DateGroup,
  DoctorRow,
  DateLabelColumn,
  DoctorRowsContainer,
  ShiftArea,
} from '../../MonthlyDoctorsShift.styles';
import { TimelineHeader } from './TimelineHeader';
import { ShiftBar } from './ShiftBar';
import { useTimelinePosition } from '../../hooks/useTimelinePosition';

/**
 * TimelineView Props
 */
interface TimelineViewProps {
  /** 表示するシフト一覧 */
  shifts: DoctorShift[];
  /** 表示対象日付の配列 */
  dates: Date[];
  /** シフトバークリックハンドラー */
  onShiftClick: (shift: DoctorShift) => void;
  /** タイムライン空き領域ダブルクリックハンドラー */
  onDoubleClick: (date: string, time: string) => void;
}

/**
 * タイムラインビューコンポーネント
 *
 * @description
 * 医師シフトをガントチャート形式で表示するメインコンポーネント。
 * 07:00〜22:00の時間軸を横方向に、各日付を縦方向に配置。
 *
 * @param {TimelineViewProps} props - コンポーネントProps
 * @returns {React.ReactElement} タイムラインビュー
 *
 * @example
 * <TimelineView
 *   shifts={shifts}
 *   dates={datesInRange}
 *   onShiftClick={handleShiftClick}
 *   onDoubleClick={handleDoubleClick}
 * />
 */
const TimelineView: React.FC<TimelineViewProps> = memo(
  ({ shifts, dates, onShiftClick, onDoubleClick }) => {
    const { calculatePosition, config } = useTimelinePosition();

    // 過去日付かどうか判定
    const isPastDate = useCallback((date: Date) => {
      return isBefore(date, startOfDay(new Date()));
    }, []);

    // シフト位置を計算
    const positions = useMemo(() => {
      const positionMap = new Map<string, { left: number; width: number }>();
      shifts.forEach((shift) => {
        const position = calculatePosition(shift.start_time, shift.end_time);
        positionMap.set(shift.id, position);
      });
      return positionMap;
    }, [shifts, calculatePosition]);

    // 日付→医師の2段階でグループ化
    const shiftsByDateAndDoctor = useMemo(() => {
      const grouped = new Map<string, Map<string, DoctorShift[]>>();
      
      // 日付ごとの初期化
      dates.forEach((date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        grouped.set(dateStr, new Map<string, DoctorShift[]>());
      });
      
      // シフトを日付→医師でグループ化
      shifts.forEach((shift) => {
        const dateMap = grouped.get(shift.shift_date);
        if (dateMap) {
          const doctorId = shift.doctor_id;
          const existing = dateMap.get(doctorId) || [];
          dateMap.set(doctorId, [...existing, shift]);
        }
      });
      
      return grouped;
    }, [shifts, dates]);

    /**
     * タイムラインダブルクリックハンドラー
     */
    const handleDoubleClick = useCallback(
      (dateString: string, event: React.MouseEvent<HTMLDivElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const totalWidth = rect.width;
        const percentX = clickX / totalWidth;

        // クリック位置から時間を計算（15分単位で丸める）
        const totalMinutes = (config.dayEndHour - config.dayStartHour) * 60;
        const minutesFromStart = Math.floor(percentX * totalMinutes);
        const roundedMinutes = Math.round(minutesFromStart / 15) * 15;
        const hour = config.dayStartHour + Math.floor(roundedMinutes / 60);
        const minute = roundedMinutes % 60;
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

        onDoubleClick(dateString, time);
      },
      [onDoubleClick, config]
    );

    return (
      <TimelineContainer>
        {/* 時間軸ヘッダー */}
        <TimelineHeader />

        {/* 日付ごとのシフトグループ */}
        {dates.map((date) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const dateLabel = format(date, 'M/d(E)', { locale: ja });
          const doctorMap = shiftsByDateAndDoctor.get(dateStr) || new Map<string, DoctorShift[]>();
          const readOnly = isPastDate(date);
          const doctorEntries = Array.from(doctorMap.entries());

          // シフトがない日付の場合は空行を1つ表示
          if (doctorEntries.length === 0) {
            return (
              <DateGroup key={dateStr}>
                <DateLabelColumn>
                  <Typography variant="body2" noWrap title={dateLabel}>
                    {dateLabel}
                  </Typography>
                </DateLabelColumn>
                <DoctorRowsContainer>
                  <DoctorRow>
                    <ShiftArea
                      onDoubleClick={!readOnly ? (e) => handleDoubleClick(dateStr, e) : undefined}
                    />
                  </DoctorRow>
                </DoctorRowsContainer>
              </DateGroup>
            );
          }

          // 医師ごとに行を生成
          return (
            <DateGroup key={dateStr}>
              {/* 日付ラベル（全行分を結合） */}
              <DateLabelColumn>
                <Typography variant="body2" noWrap title={dateLabel}>
                  {dateLabel}
                </Typography>
              </DateLabelColumn>

              {/* 医師行コンテナ */}
              <DoctorRowsContainer>
                {doctorEntries.map(([doctorId, doctorShifts]) => (
                  <DoctorRow key={`${dateStr}-${doctorId}`}>
                    <ShiftArea
                      onDoubleClick={!readOnly ? (e) => handleDoubleClick(dateStr, e) : undefined}
                    >
                      {doctorShifts.map((shift) => {
                        const position = positions.get(shift.id);
                        if (!position) return null;

                        return (
                          <ShiftBar
                            key={shift.id}
                            shift={shift}
                            position={position}
                            onClick={onShiftClick}
                            readOnly={readOnly}
                          />
                        );
                      })}
                    </ShiftArea>
                  </DoctorRow>
                ))}
              </DoctorRowsContainer>
            </DateGroup>
          );
        })}

        {/* シフトがない場合（全日付通じてシフトがない） */}
        {shifts.length === 0 && dates.length > 0 && (
          <EmptyState>
            <Box>該当するシフトがありません</Box>
            <Box sx={{ fontSize: '0.875rem', mt: 1 }}>
              タイムラインをダブルクリックしてシフトを追加できます
            </Box>
          </EmptyState>
        )}
      </TimelineContainer>
    );
  }
);

TimelineView.displayName = 'TimelineView';

export { TimelineView };
